import Vue from 'vue'
import App from './App.vue'
import router from './router'
import store from './store'
import { Modal, notification, Tooltip, Button, message } from 'ant-design-vue'
import './assets/css/main.css'
import './assets/css/iconfont.css'
import './assets/css/theme.css'
import './assets/css/overrides.css'
import 'primeflex/primeflex.css'
import PermissionGuard from './plugins/permissionGuard'
import { googleMapsPlugin, googleMapsPluginOptions } from './plugins/google-maps'
import { headMetaPlugin } from './plugins/head-meta'
import { inputMaskPlugin } from './plugins/input-mask'
import { maybeLoadVueTourPlugin } from './plugins/vue-tour'
import 'vue-phone-number-input/dist/vue-phone-number-input.css'
import ButtonMore from './components/ui/button-more.vue'
import AppIcon from './components/ui/app-icon.vue'
import TableHeader from './components/entities/table/table-header.vue'
import TableLayout from './components/entities/table/table-layout.vue'
import ActiveTableFilters from './components/entities/table/active-table-filters.vue'
import * as Sentry from '@sentry/vue'
import { initModalZIndexManager } from './utils/modal-z-index-manager'
import ModalFullscreen from './components/entities/modal/modal-fullscreen.vue'
import { INTERNET_CONNECTION_ERROR_MESSAGE } from '@/services/internet-connection'
import { APP_UPDATED_NOTIFICATION_MESSAGE } from '@/services/server-update-notification'
import { captureUnexpectedError, hasUnexpectedErrorNotification, initSupportDiagnostics } from '@/services/support-diagnostics'

const SENTRY_PRODUCTION_HOST = 'app.epeople.ai'

const getSentryHostname = () => window?.location?.hostname || ''

const getSentryEnvironment = () => {
  return getSentryHostname() === SENTRY_PRODUCTION_HOST ? 'production' : 'qa'
}

const getSentryReplaySessionSampleRate = () => {
  return getSentryEnvironment() === 'production' ? 0.25 : 1.0
}

const sanitizeUrlPath = value => {
  if (!value || typeof value !== 'string') {
    return 'unknown'
  }

  try {
    const url = new URL(value, window.location.origin)

    return url.pathname
      .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/gi, ':uuid')
      .replace(/\/\d+(?=\/|$)/g, '/:id')
  } catch (e) {
    return value
      .split('?')[0]
      .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/gi, ':uuid')
      .replace(/\/\d+(?=\/|$)/g, '/:id')
  }
}

const getSafeRouteContext = route => ({
  name: route?.name || 'unknown',
  path: sanitizeUrlPath(route?.path || 'unknown')
})

const sanitizeBreadcrumbData = data => {
  if (!data || typeof data !== 'object') {
    return data
  }

  const safeData = {}
  const allowedKeys = ['url', 'method', 'status', 'status_code', 'reason', 'shouldCallSignOutApi']

  allowedKeys.forEach(key => {
    if (data[key] === undefined) {
      return
    }

    safeData[key] = key === 'url' ? sanitizeUrlPath(data[key]) : data[key]
  })

  return safeData
}

const sanitizeBreadcrumbMessage = breadcrumb => {
  if (breadcrumb.category === 'auth.cleanup') {
    return breadcrumb.message
  }

  return breadcrumb.category || breadcrumb.type || 'breadcrumb'
}

const isSentryHttpClientErrorEvent = event => {
  const exceptions = event?.exception?.values || []

  return exceptions.some(exception => /^HTTP Client Error with status code: \d+/.test(exception?.value || ''))
}

const sanitizeSentryEvent = event => {
  if (isSentryHttpClientErrorEvent(event)) {
    return null
  }

  if (event.request) {
    delete event.request.cookies
    delete event.request.data
    delete event.request.headers
    delete event.request.query_string

    if (event.request.url) {
      event.request.url = sanitizeUrlPath(event.request.url)
    }
  }

  if (event.user) {
    event.user = event.user.id ? { id: String(event.user.id) } : undefined
  }

  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map(breadcrumb => ({
      ...breadcrumb,
      message: sanitizeBreadcrumbMessage(breadcrumb),
      data: sanitizeBreadcrumbData(breadcrumb.data)
    }))
  }

  return event
}

if (window && getSentryHostname().endsWith('epeople.ai')) {
  Sentry.init({
    Vue: Vue,
    dsn: process.env.SENTRY_DSN,
    sendDefaultPii: false,
    environment: getSentryEnvironment(),
    integrations: [
      Sentry.feedbackIntegration({
        autoInject: false
      }),
      Sentry.browserTracingIntegration({ router }),
      Sentry.httpContextIntegration(),
      Sentry.httpClientIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
        maxReplayDuration: 120000
      }),
      Sentry.consoleLoggingIntegration({ levels: ['error'] })
    ],
    tracesSampleRate: 0.5,
    tracePropagationTargets: ['localhost', /^\/api\//, 'app.epeople.ai'],
    enableTracing: true,
    enableLogs: true,
    attachStackTrace: true,
    beforeSend: sanitizeSentryEvent,
    replaysSessionSampleRate: getSentryReplaySessionSampleRate(),
    replaysOnErrorSampleRate: 1.0
  })
}

Vue.use(headMetaPlugin)
Vue.config.productionTip = false

Vue.use(Modal)
Vue.use(inputMaskPlugin)
Vue.use(PermissionGuard, { router, store, notification })
Vue.use(googleMapsPlugin, googleMapsPluginOptions)
Vue.component('button-more', ButtonMore)
Vue.component('table-header', TableHeader)
Vue.component('table-layout', TableLayout)
Vue.component('active-table-filters', ActiveTableFilters)
Vue.component('a-icon', AppIcon)
Vue.component('modal-fullscreen', ModalFullscreen)
Vue.component('a-tooltip', Tooltip)
Vue.component('a-button', Button)

const initialPath = window?.location?.pathname || ''
const initialFullPath = `${initialPath}${window?.location?.search || ''}`

maybeLoadVueTourPlugin(Vue, {
  path: initialPath,
  fullPath: initialFullPath
})

router.beforeEach((to, from, next) => {
  maybeLoadVueTourPlugin(Vue, to)
  next()
})

Vue.prototype.$tableHeight = Vue.observable({
  value: window.innerHeight - 320
})

window.addEventListener('resize', () => {
  Vue.prototype.$tableHeight.value = window.innerHeight - 320
})

Vue.mixin({
  mounted() {
    if (this.onKeydown) {
      window.addEventListener('keydown', this.onKeydown)
    }
  },
  beforeDestroy() {
    if (this.onKeydown) {
      window.removeEventListener('keydown', this.onKeydown)
    }
  }
})

notification.config({
  duration: 10
})

const isInternetConnectionMessage = value =>
  typeof value === 'string' && value.trim() === INTERNET_CONNECTION_ERROR_MESSAGE

const isAppUpdatedNotificationMessage = value =>
  typeof value === 'string' && value.trim() === APP_UPDATED_NOTIFICATION_MESSAGE

const isInternetConnectionNotificationConfig = config =>
  config &&
  typeof config === 'object' &&
  (isInternetConnectionMessage(config.description) ||
    isInternetConnectionMessage(config.message) ||
    isInternetConnectionMessage(config.content))

const isAppUpdatedNotificationConfig = config =>
  config &&
  typeof config === 'object' &&
  (isAppUpdatedNotificationMessage(config.description) ||
    isAppUpdatedNotificationMessage(config.message) ||
    isAppUpdatedNotificationMessage(config.content))

const wrapNotificationMethod = methodName => {
  const originalMethod = notification[methodName]

  notification[methodName] = function (config, ...args) {
    if (isAppUpdatedNotificationMessage(config) || isAppUpdatedNotificationConfig(config)) {
      if (methodName === 'info') {
        return originalMethod.call(this, config, ...args)
      }

      return
    }

    if (isInternetConnectionMessage(config) || isInternetConnectionNotificationConfig(config)) {
      return
    }

    if (
      (methodName === 'error' || (methodName === 'open' && config?.type === 'error')) &&
      hasUnexpectedErrorNotification(config?.description, config?.key)
    ) {
      return
    }

    return originalMethod.call(this, config, ...args)
  }
}

const notificationMethodsToWrap = ['error', 'warning', 'warn', 'info', 'open']
notificationMethodsToWrap.forEach(wrapNotificationMethod)

const originalMessageError = message.error

message.error = function (content, ...args) {
  if (isAppUpdatedNotificationMessage(content) || isAppUpdatedNotificationConfig(content)) {
    const noop = () => {}
    noop.promise = Promise.resolve()
    noop.then = noop.promise.then.bind(noop.promise)

    return noop
  }

  if (
    isInternetConnectionMessage(content) ||
    isInternetConnectionNotificationConfig(content) ||
    hasUnexpectedErrorNotification(typeof content === 'string' ? content : content?.content)
  ) {
    const noop = () => {}
    noop.promise = Promise.resolve()
    noop.then = noop.promise.then.bind(noop.promise)

    return noop
  }

  return originalMessageError.call(this, content, ...args)
}

initSupportDiagnostics(store)

Vue.config.errorHandler = function (err, vm, info) {
  captureUnexpectedError(err, {
    contexts: {
      vue: {
        component: vm?.$options?.name || 'Anonymous',
        props: vm?.$options?.propsData || {},
        info: info,
        route: getSafeRouteContext(router.currentRoute),
        action: 'vue_error_handler',
        errorClass: err?.name || 'Error'
      }
    }
  })
}

new Vue({
  router,
  store,
  render: h => h(App)
}).$mount('#app')

initModalZIndexManager()
